<script setup lang="ts">
// `DataExportStep2.vue:62-135` + `:385-458` — the Metric Configuration block.
//
// Only metrics that declare `event_slug` or `cohort_period` are configurable (`:387-389`). The
// panel owns no state of its own: every edit is written straight back onto the selected column
// through `applyMetricConfig`, and re-read through `readMetricConfig`, so the `<d|w|m><n>` cohort
// code round-trips across a reload without a shadow `metricConfigs` map (`:391-408`).
import { computed, ref } from 'vue'

import {
  COHORT_PERIOD_LABEL_KEYS,
  COHORT_PERIOD_TYPES,
  applyMetricConfig,
  readMetricConfig,
  type CohortPeriodType,
  type MetricConfig,
} from '../../composables/useExportColumns'
import type { ExportColumnSelection } from '../../models/exportJob.types'

import { useI18n } from '@/shared/i18n'
import { Input } from '@/shared/ui/input'
import { MultiSelect } from '@/shared/ui/multi-select'
import { Select } from '@/shared/ui/select'
import type { SelectOption } from '@/shared/ui/select'

const props = withDefaults(
  defineProps<{
    columns: readonly ExportColumnSelection[]
    availableEvents?: readonly string[]
  }>(),
  { availableEvents: () => [] },
)
const emit = defineEmits<{ 'update:columns': [columns: ExportColumnSelection[]] }>()
const { t } = useI18n()

const configurableMetrics = computed(() =>
  props.columns.filter((column) => column.numeric && (column.event_slug || column.cohort_period)),
)
const eventOptions = computed<SelectOption<string>[]>(() =>
  props.availableEvents.map((event) => ({ value: event, label: event })),
)
const cohortTypeOptions = computed<SelectOption<CohortPeriodType>[]>(() =>
  COHORT_PERIOD_TYPES.map((type) => ({ value: type, label: t(COHORT_PERIOD_LABEL_KEYS[type]) })),
)

// The encoded `<d|w|m><n>` cannot hold a period TYPE on its own, so picking "Week" before typing a
// length would be forgotten. Legacy kept the same pair in a shadow `metricConfigs` map (`:391-408`);
// only the not-yet-encodable half is held here, and the column stays the source of truth.
const pendingCohortType = ref<Record<string, CohortPeriodType>>({})

function config(column: ExportColumnSelection): MetricConfig {
  const stored = readMetricConfig(column)
  const pending = pendingCohortType.value[column.field]
  return pending === undefined ? stored : { ...stored, cohortType: pending }
}

function eventSlug(column: ExportColumnSelection): string[] {
  return config(column).eventSlug
}

function cohortType(column: ExportColumnSelection): CohortPeriodType {
  return config(column).cohortType
}

function cohortValue(column: ExportColumnSelection): string | number {
  return config(column).cohortValue ?? ''
}

function toNumberOrNull(value: string | number | undefined): number | null {
  if (value === undefined || value === '') return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

function setEventSlug(column: ExportColumnSelection, value: string[]): void {
  emit(
    'update:columns',
    applyMetricConfig(props.columns, column.field, {
      ...config(column),
      eventSlug: value,
    }),
  )
}

function setCohortType(column: ExportColumnSelection, value: CohortPeriodType | null): void {
  if (value === null) return
  pendingCohortType.value = { ...pendingCohortType.value, [column.field]: value }
  emit(
    'update:columns',
    applyMetricConfig(props.columns, column.field, {
      ...config(column),
      cohortType: value,
    }),
  )
}

function setCohortValue(column: ExportColumnSelection, value: string | number | undefined): void {
  emit(
    'update:columns',
    applyMetricConfig(props.columns, column.field, {
      ...config(column),
      cohortValue: toNumberOrNull(value),
    }),
  )
}
</script>

<template>
  <section
    v-if="configurableMetrics.length > 0"
    class="metric-config grid gap-sm"
    :aria-label="t('dataExport.data.metricConfig.title')"
  >
    <h3 class="metric-config__title text-sm font-semibold">
      {{ t('dataExport.data.metricConfig.title') }}
    </h3>

    <div
      v-for="metric in configurableMetrics"
      :key="metric.field"
      class="metric-config__row grid gap-xs pb-sm border-b border-b-border [&:last-child]:border-b-0"
    >
      <p class="metric-config__metric font-semibold">{{ metric.header }}</p>

      <MultiSelect
        v-if="metric.event_slug"
        :model-value="eventSlug(metric)"
        :options="eventOptions"
        :label="t('dataExport.data.metricConfig.eventLabel')"
        :placeholder="t('dataExport.data.metricConfig.eventPlaceholder')"
        :empty-label="t('dataExport.common.noResults')"
        searchable
        @update:model-value="setEventSlug(metric, $event)"
      />

      <div v-if="metric.cohort_period" class="metric-config__cohort grid grid-cols-2 gap-sm">
        <Select
          :model-value="cohortType(metric)"
          :options="cohortTypeOptions"
          :label="t('dataExport.data.metricConfig.cohortTypeLabel')"
          :empty-label="t('dataExport.common.noResults')"
          @update:model-value="setCohortType(metric, $event)"
        />
        <Input
          :model-value="cohortValue(metric)"
          type="number"
          :min="1"
          :label="t('dataExport.data.metricConfig.cohortValueLabel')"
          @update:model-value="setCohortValue(metric, $event)"
        />
      </div>
    </div>
  </section>
</template>
